import { useMemo, useState } from 'react';
import { Archive, Download, History } from 'lucide-react';
import {
  carregarHistoricoBolao,
  exportarHistoricoJson,
  type SnapshotHistoricoBolao,
} from '../../utils/historicoBolaoCopa';
import { formatarReais } from '../../utils/copaPremiacao';

function formatarQuando(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  } catch {
    return iso;
  }
}

export function HistoricoBolaoCopaPanel({
  historico,
  onExportar,
}: {
  historico: SnapshotHistoricoBolao[];
  onExportar?: () => void;
}) {
  const ordenado = useMemo(
    () =>
      [...historico].sort(
        (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
      ),
    [historico]
  );
  const [selecionadoId, setSelecionadoId] = useState<string>(
    () => ordenado[0]?.id ?? ''
  );

  const snap = ordenado.find((h) => h.id === selecionadoId) ?? ordenado[0] ?? null;

  const baixarJson = () => {
    const txt = exportarHistoricoJson();
    const blob = new Blob([txt], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historico-bolao-copa-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    onExportar?.();
  };

  if (ordenado.length === 0) {
    return (
      <div className="ade-neu-card p-6 text-center border border-[#009b3a]/20">
        <History className="w-10 h-10 text-neutral-400 mx-auto mb-2" />
        <p className="text-sm text-neutral-600">
          Nenhum snapshot ainda. Quando um jogo terminar e o resultado real for
          registrado, a tabela de posição e a premiação daquele momento ficam
          salvas aqui automaticamente.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="ade-neu-card p-4 border-2 border-[#009b3a]/25 bg-[#f5fff8]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="font-bold text-black flex items-center gap-2">
              <Archive className="w-5 h-5 text-[#009b3a]" />
              Histórico de posição e premiação
            </h4>
            <p className="text-xs text-neutral-700 mt-1 max-w-2xl">
              Cópia de segurança local após cada jogo encerrado ({ordenado.length}{' '}
              registro{ordenado.length === 1 ? '' : 's'}). Se houver erro no futuro,
              consulte aqui como estava a tabela naquele momento.
            </p>
          </div>
          <button
            type="button"
            onClick={baixarJson}
            className="px-3 py-2 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 border-2 border-[#009b3a] bg-white text-[#009b3a] hover:bg-[#009b3a]/10"
          >
            <Download className="w-4 h-4" />
            Baixar JSON
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(220px,280px)_1fr]">
        <div className="ade-neu-card border border-[#009b3a]/15 overflow-hidden max-h-[520px] overflow-y-auto">
          <p className="text-[10px] font-bold uppercase text-neutral-500 px-3 py-2 border-b border-neutral-200 bg-neutral-50 sticky top-0">
            Jogos (mais recente primeiro)
          </p>
          <ul className="divide-y divide-neutral-100">
            {ordenado.map((h) => {
              const ativo = h.id === snap?.id;
              return (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => setSelecionadoId(h.id)}
                    className={`w-full text-left px-3 py-2.5 text-xs transition ${
                      ativo
                        ? 'bg-[#009b3a]/10 border-l-4 border-l-[#009b3a]'
                        : 'hover:bg-neutral-50 border-l-4 border-l-transparent'
                    }`}
                  >
                    <p className="font-semibold text-black leading-snug">{h.jogoRotulo}</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">{h.jogoDetalhe}</p>
                    <p className="text-[10px] text-neutral-400 mt-1">
                      {formatarQuando(h.criadoEm)} · {h.totalJogosComResultado} jogo(s) contados
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {snap && (
          <div className="ade-neu-card border border-[#009b3a]/15 overflow-hidden">
            <div className="px-4 py-3 bg-[#009b3a] text-white">
              <p className="font-bold text-sm">{snap.jogoRotulo}</p>
              <p className="text-[11px] text-white/85 mt-0.5">{snap.jogoDetalhe}</p>
              <p className="text-[10px] text-white/70 mt-1">
                Snapshot: {formatarQuando(snap.criadoEm)}
              </p>
            </div>
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-neutral-100 text-neutral-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">#</th>
                    <th className="text-left px-3 py-2 font-bold">Participante</th>
                    <th className="text-right px-3 py-2 font-bold">Pts</th>
                    <th className="text-right px-3 py-2 font-bold">Faltas</th>
                    <th className="text-right px-3 py-2 font-bold">Prêmio</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.linhas.map((l) => (
                    <tr
                      key={l.id}
                      className={`border-t border-neutral-100 ${
                        l.posicao <= 3 ? 'bg-amber-50/40' : ''
                      }`}
                    >
                      <td className="px-3 py-2 font-bold tabular-nums">{l.posicao}º</td>
                      <td className="px-3 py-2">
                        <span className="font-semibold text-black">{l.nome}</span>
                        {l.setor && (
                          <span className="text-neutral-500 ml-1">({l.setor})</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-[#009b3a]">
                        {l.pontos}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{l.faltas}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-800">
                        {formatarReais(l.premioFinal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
